import React, {memo, useState} from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Collapse from '@mui/material/Collapse';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LayersOutlinedIcon from '@mui/icons-material/LayersOutlined';
import StorageOutlinedIcon from '@mui/icons-material/StorageOutlined';
import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined';
import SmartphoneOutlinedIcon from '@mui/icons-material/SmartphoneOutlined';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import CloudOutlinedIcon from '@mui/icons-material/CloudOutlined';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import SettingsSuggestOutlinedIcon from '@mui/icons-material/SettingsSuggestOutlined';
import NorthEastIcon from '@mui/icons-material/NorthEast';
import Image from 'next/image';
import MUIContainer from '@/components/MUI-Components/MUI-Container';
import DisplayImage from '@/components/Ui-components/display-image';
import Grid from '@mui/material/Grid';
import parse from 'html-react-parser';
import {
    colors,
    fontWeight,
    fontFamily,
    fontSize,
    borderRadius,
    letterSpacing,
    overlay,
    shadows,
    transition,
    lineHeight,
    spacing
} from '@/styles/tokens';
import type {SvgIconProps} from '@mui/material/SvgIcon';
import Eyebrow from '@/components/Common/Eyebrow';

// ─── Icon map for grid items ──────────────────────────────────────────────────
const ICON_MAP: Record<string, React.ComponentType<SvgIconProps>> = {
    layers: LayersOutlinedIcon,
    storage: StorageOutlinedIcon,
    dashboard: DashboardOutlinedIcon,
    phone: SmartphoneOutlinedIcon,
    ai: AutoAwesomeOutlinedIcon,
    cloud: CloudOutlinedIcon,
    qa: FactCheckOutlinedIcon,
    lock: LockOutlinedIcon,
    settings: SettingsSuggestOutlinedIcon
};

// ─── Types ────────────────────────────────────────────────────────────────────
export interface EngineeringLeadersAward {
    year: string;
    isLatest?: boolean;
    platform: string;
    location: string;
    category: string;
    description: string;
    link?: string;
    variant?: 'light' | 'dark';
}

export interface EngineeringLeadersGridItem {
    label: string;
    iconKey: keyof typeof ICON_MAP;
    iconSrc?: string;
}

export interface EngineeringLeadersItem {
    title: string;
    subheading: string;
    awards?: EngineeringLeadersAward[];
    gridItems?: EngineeringLeadersGridItem[];
    tags?: string[];
}

export interface EngineeringLeadersTopCard {
    /** Top visual — image URL/StaticImageData. Leave empty; placeholder space is reserved. */
    image?: string;
    title: string;
    description: string;
}

interface EngineeringLeadersChooseProps {
    eyebrow?: string;
    heading?: string;
    subheading?: string;
    topCards?: EngineeringLeadersTopCard[];
    items: EngineeringLeadersItem[];
}

// ─── Section header sx ────────────────────────────────────────────────────────
// ─── Top cards sx (same style as blockchain "Why Choose Us" cards) ────────────
const topCardSx = {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    textAlign: 'center' as const,
    border: `1px solid ${colors.neutral.borderLight}`,
    borderRadius: borderRadius.lg,
    py: {xs: 4, md: 4},
    px: {xs: 2, md: 2.5},
    height: '100%',
    bgcolor: colors.background.default,
    boxShadow: shadows.elevated
} as const;

const topCardImageWrapperSx = {
    position: 'relative' as const,
    width: '100%',
    height: 170,
    mb: 4,
    transition: 'transform 0.3s ease',
    '&:hover': {transform: 'scale(1.08)'}
} as const;

const topCardTitleSx = {mb: 2, color: colors.text.primary, fontSize: fontSize[20]} as const;
const topCardDescSx = {lineHeight: 1.6, color: colors.text.primary} as const;
const topCardsGridMbSx = {mb: spacing[40]} as const;

// ─── Accordion row sx ─────────────────────────────────────────────────────────
const accordionRowSx = {
    bgcolor: colors.background.default,
    borderBottom: `1px solid ${colors.neutral.borderLight}`,
    borderRadius: borderRadius.lg,
    mb: spacing[16],
    overflow: 'hidden'
} as const;

const accordionHeaderSx = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    px: spacing[20],
    py: spacing[16],
    cursor: 'pointer',
    userSelect: 'none' as const,
    gap: spacing[16]
} as const;

// Line-height sits tight to the 20px text: at 32px the h3's own leading added ~6px
// of dead space under the title, so the title→copy gap read wider than copy→pills.
// At 24px every item in the row is an even 16px apart.
const accordionTitleSx = {
    fontSize: {xs: fontSize[18], md: fontSize[20]},
    lineHeight: lineHeight[24],
    fontFamily: fontFamily.heading
} as const;

// 16px body text on a 28px line-height: the previous 18/32 pairing left the copy
// floating well clear of both the title above and the pills below.
const accordionSubheadingSx = {
    fontSize: fontSize[16],
    color: colors.text.primary,
    lineHeight: lineHeight[28],
    fontFamily: fontFamily.body,
    mt: 0,
    mb: spacing[16]
} as const;

const accordionBodySx = {
    p: `0 ${spacing[20]} ${spacing[20]}`
} as const;

// ─── Award card sx ────────────────────────────────────────────────────────────
const awardsGridSx = {
    display: 'grid',
    gridTemplateColumns: {xs: '1fr', sm: '1fr 1fr', md: 'repeat(3, 1fr)'},
    gap: spacing[24]
} as const;

const awardCardBase = {
    borderRadius: borderRadius.xl,
    p: spacing[24],
    position: 'relative' as const,
    overflow: 'hidden',
    boxShadow: shadows.sm,
    height: '100%',
    display: 'flex',
    flexDirection: 'column' as const
} as const;

const awardCardLightSx = {
    ...awardCardBase,
    bgcolor: colors.background.default,
    border: `1px solid ${colors.neutral.grayLight}`
} as const;

const awardCardDarkSx = {
    ...awardCardBase,
    bgcolor: colors.primary.main,
    border: 'none',
    boxShadow: shadows.awardCardElevated
} as const;

const awardYearBadgeLightSx = {
    display: 'inline-flex',
    alignItems: 'center',
    bgcolor: colors.primary.main,
    borderRadius: borderRadius.lg,
    px: spacing[16],
    py: spacing[8],
    mb: spacing[20],
    alignSelf: 'flex-start'
} as const;

const awardYearBadgeDarkSx = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: spacing[8],
    bgcolor: overlay.whiteLight,
    borderRadius: borderRadius.lg,
    px: spacing[16],
    py: spacing[8],
    mb: spacing[20],
    alignSelf: 'flex-start'
} as const;

const awardLatestPillSx = {
    bgcolor: colors.accent.gold,
    borderRadius: borderRadius.sm,
    px: spacing[8],
    py: spacing[4],
    display: 'inline-flex',
    alignItems: 'center'
} as const;

const awardLatestLabelSx = {
    fontSize: fontSize[14],
    fontWeight: fontWeight.regular,
    color: colors.accent.goldDark,
    fontFamily: fontFamily.body
} as const;

const awardYearLabelSx = {
    fontSize: fontSize[24],
    fontWeight: fontWeight.regular,
    color: colors.text.inverse,
    lineHeight: lineHeight[32],
    letterSpacing: letterSpacing.normal,
    fontFamily: fontFamily.body
} as const;

const awardPlatformBlockSx = {mb: spacing[16]} as const;

const topCardsGridItemSx = {display: 'flex'} as const;

const capabilityIconSx = {fontSize: fontSize[18], color: colors.primary.main} as const;

const northEastIconSx = {fontSize: fontSize[16]} as const;

// ─── Grid items sx ────────────────────────────────────────────────────────────
const capabilityGridSx = {
    display: 'grid',
    gridTemplateColumns: {xs: '1fr', sm: '1fr 1fr', md: 'repeat(3, 1fr)'},
    gap: spacing[16]
} as const;

const capabilityItemSx = {
    display: 'flex',
    alignItems: 'center',
    gap: spacing[12],
    bgcolor: colors.background.subtle,
    borderRadius: borderRadius.xl,
    px: spacing[12],
    py: spacing[12],
    height: spacing[60],
    border: `1px solid ${overlay.whiteTenth}`
} as const;

const capabilityIconWrapSx = {
    bgcolor: colors.primary.iconBg,
    borderRadius: borderRadius.lg,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: spacing[32],
    height: spacing[32],
    minWidth: spacing[32],
    flexShrink: 0
} as const;

const capabilityLabelSx = {
    fontSize: fontSize[16],
    fontWeight: fontWeight.medium,
    color: colors.text.primary,
    lineHeight: lineHeight[20],
    fontFamily: fontFamily.body
} as const;

// ─── Tags sx ─────────────────────────────────────────────────────────────────
const tagsWrapSx = {
    display: 'flex',
    flexWrap: 'wrap' as const,
    gap: spacing[12]
} as const;

// Same badge treatment as the `KeyServices` service badges — the site-wide pill style.
const tagPillSx = {
    border: `1px solid ${colors.neutral.borderLight}`,
    bgcolor: colors.background.tag,
    borderRadius: borderRadius.full,
    px: spacing[16],
    py: spacing[8],
    display: 'inline-flex',
    alignItems: 'center'
} as const;

const tagLabelSx = {
    fontSize: fontSize[14],
    fontWeight: fontWeight.semibold,
    color: colors.primary.deep,
    lineHeight: lineHeight[20],
    whiteSpace: 'nowrap' as const
} as const;

// ─── Sub-components ────────────────────────────────────────────────────────────

const AwardCard = ({award}: {award: EngineeringLeadersAward}) => {
    const isDark = award.variant === 'dark';

    return (
        <Box sx={isDark ? awardCardDarkSx : awardCardLightSx}>
            {/* Decorative corner gradient */}
            <Box
                sx={{
                    position: 'absolute',
                    top: 0,
                    right: 0,
                    width: 100,
                    height: 110,
                    borderBottomLeftRadius: borderRadius.full,
                    backgroundColor: isDark ? overlay.whiteTenth : colors.primary.light,
                    pointerEvents: 'none'
                }}
            />

            {/* Year badge */}
            <Box sx={isDark ? awardYearBadgeDarkSx : awardYearBadgeLightSx}>
                <Typography sx={awardYearLabelSx}>{award.year}</Typography>
                {award.isLatest && (
                    <Box sx={awardLatestPillSx}>
                        <Typography sx={awardLatestLabelSx}>Latest</Typography>
                    </Box>
                )}
            </Box>

            {/* Platform + location */}
            <Box sx={awardPlatformBlockSx}>
                <Typography
                    sx={{
                        fontSize: fontSize[16],
                        color: isDark ? colors.text.inverse : colors.text.primary,
                        lineHeight: lineHeight[24],
                        fontFamily: fontFamily.body,
                        letterSpacing: letterSpacing.snug
                    }}
                >
                    {award.platform}
                </Typography>
                <Typography
                    sx={{
                        fontSize: fontSize[14],
                        color: isDark ? overlay.whiteSubtitle : colors.text.muted,
                        lineHeight: lineHeight[20],
                        fontFamily: fontFamily.body,
                        letterSpacing: letterSpacing.normal,
                        mt: spacing[4]
                    }}
                >
                    {award.location}
                </Typography>
            </Box>

            {/* Category title */}
            <Typography
                component="h3"
                sx={{
                    fontSize: fontSize[18],
                    fontWeight: fontWeight.medium,
                    color: isDark ? colors.text.inverse : colors.text.primary,
                    lineHeight: lineHeight[28],
                    letterSpacing: letterSpacing.snug,
                    fontFamily: fontFamily.heading
                }}
            >
                {award.category}
            </Typography>

            {/* Description */}
            <Typography
                sx={{
                    fontSize: fontSize[16],
                    color: isDark ? overlay.whiteText : colors.text.primary,
                    lineHeight: lineHeight[28],
                    fontFamily: fontFamily.body,
                    letterSpacing: letterSpacing.snug,
                    flexGrow: 1
                }}
            >
                {award.description}
            </Typography>

            {/* Link */}
            {award.link && (
                <Box
                    component="a"
                    href={award.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: spacing[8],
                        color: isDark ? colors.text.inverse : colors.primary.main,
                        fontSize: fontSize[16],
                        fontFamily: fontFamily.body,
                        textDecoration: 'none',
                        letterSpacing: letterSpacing.snug,
                        '&:hover': {textDecoration: 'underline'}
                    }}
                >
                    View Source Publication
                    <NorthEastIcon sx={northEastIconSx} />
                </Box>
            )}
        </Box>
    );
};

const CapabilityGrid = ({items}: {items: EngineeringLeadersGridItem[]}) => (
    <Box sx={capabilityGridSx}>
        {items.map((item, i) => {
            const IconComponent = ICON_MAP[item.iconKey] ?? LayersOutlinedIcon;
            return (
                <Box key={i} sx={capabilityItemSx}>
                    <Box sx={capabilityIconWrapSx}>
                        {item.iconSrc ? (
                            <Image src={item.iconSrc} alt={item.label} width={20} height={20} style={{objectFit: 'contain'}} />
                        ) : (
                            <IconComponent sx={capabilityIconSx} />
                        )}
                    </Box>
                    <Typography sx={capabilityLabelSx}>{item.label}</Typography>
                </Box>
            );
        })}
    </Box>
);

const TagPills = ({tags}: {tags: string[]}) => (
    <Box sx={tagsWrapSx}>
        {tags.map((tag, i) => (
            <Box key={i} sx={tagPillSx}>
                <Typography className="font-headers" sx={tagLabelSx}>
                    {tag}
                </Typography>
            </Box>
        ))}
    </Box>
);

const AccordionRow = ({item, expanded, onToggle}: {item: EngineeringLeadersItem; expanded: boolean; onToggle: () => void}) => (
    <Box sx={accordionRowSx}>
        <Box
            sx={accordionHeaderSx}
            onClick={onToggle}
            role="button"
            aria-expanded={expanded}
            tabIndex={0}
            onKeyDown={(e) => e.key === 'Enter' && onToggle()}
        >
            <Typography component="h3" sx={accordionTitleSx}>
                {item.title}
            </Typography>
            <ExpandMoreIcon
                sx={{
                    color: colors.text.primary,
                    flexShrink: 0,
                    transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: `transform ${transition.slow}`
                }}
            />
        </Box>

        {/* Kept mounted when closed (zero height, hidden): every row's content is in the HTML for search engines. */}
        <Collapse in={expanded} timeout="auto">
            <Box sx={accordionBodySx}>
                {item.subheading && (
                    <Typography sx={accordionSubheadingSx} className="font-regular-text">
                        {parse(item.subheading)}
                    </Typography>
                )}

                {item.awards && item.awards.length > 0 && <AwardCards awards={item.awards} />}
                {item.gridItems && item.gridItems.length > 0 && <CapabilityGrid items={item.gridItems} />}
                {item.tags && item.tags.length > 0 && <TagPills tags={item.tags} />}
            </Box>
        </Collapse>
    </Box>
);

const AwardCards = ({awards}: {awards: EngineeringLeadersAward[]}) => (
    <Box sx={awardsGridSx}>
        {awards.map((award, i) => (
            <AwardCard key={i} award={award} />
        ))}
    </Box>
);

// ─── Main Component ────────────────────────────────────────────────────────────

const EngineeringLeadersChoose = ({eyebrow, heading, subheading, topCards, items}: EngineeringLeadersChooseProps) => {
    const [expandedIndex, setExpandedIndex] = useState<number | null>(0);

    const toggle = (index: number) => setExpandedIndex((prev) => (prev === index ? null : index));

    return (
        <Box>
            <MUIContainer>
                {(eyebrow || heading || subheading) && (
                    <Box textAlign="center" maxWidth={1024} margin="0 auto" mb={5}>
                        {eyebrow && <Eyebrow text={eyebrow} sx={{textAlign: 'center'}} />}
                        {heading && (
                            <Typography variant="h1" component="h2" mb={3} fontSize={{xs: 35, sm: 48}} className="font-headers">
                                {parse(heading)}
                            </Typography>
                        )}
                        {subheading && <Typography className="font-regular-text">{parse(subheading)}</Typography>}
                    </Box>
                )}

                {topCards && topCards.length > 0 && (
                    <Grid container spacing={4} sx={topCardsGridMbSx}>
                        {topCards.map((card, idx) => (
                            <Grid item xs={12} md={4} key={idx} sx={topCardsGridItemSx}>
                                <Box sx={topCardSx}>
                                    <Box sx={topCardImageWrapperSx}>
                                        {card.image && (
                                            <DisplayImage src={card.image} alt={card.title} fill style={{objectFit: 'contain'}} />
                                        )}
                                    </Box>
                                    <Typography variant="h5" component="h3" sx={topCardTitleSx} className="font-headers">
                                        {card.title}
                                    </Typography>
                                    <Typography sx={topCardDescSx} className="font-text-regular remove-underline">
                                        {parse(card.description)}
                                    </Typography>
                                </Box>
                            </Grid>
                        ))}
                    </Grid>
                )}

                <Box>
                    {items.map((item, index) => (
                        <AccordionRow key={index} item={item} expanded={expandedIndex === index} onToggle={() => toggle(index)} />
                    ))}
                </Box>
            </MUIContainer>
        </Box>
    );
};

export default memo(EngineeringLeadersChoose);
